// Подъезд машины: паровоз приходит из глубины кадра на своё рабочее место.
//
// На экране интро машина стоит целиком и мелко — она вдали, см. INTRO_VIEW. Игровое поле
// начинает с той же позы и подводит машину к игроку: она вырастает до рабочего размера,
// пыхая паром, и только после этого открывает створки. Так смена экрана читается как подход
// поезда, а не как подмена картинки.
//
// Едет одна машина. Фон стоит на месте: к игроку подходит поезд, а не игрок к поезду.
// Тем же и отличается подъезд от взгляда игры, который водит машину вместе с фоном,
// см. ui/camera.ts.

import type { Container } from 'pixi.js';

import { smoothStep } from './scene';

/**
 * Сколько машина подъезжает.
 *
 * На экране интро она стоит, поэтому трогается с места и встаёт на место плавно, см. smoothStep.
 * Разгон и торможение съедают почти половину пути, и на коротком ходу от движения оставалась бы
 * одна середина. За две секунды видно и то, и другое, и это единственное ожидание за загрузку.
 */
export const ARRIVAL_SECONDS: number = 2;

/** Подъезд машины к игроку. */
export class Arrival {
  /** Кончился ли подъезд. */
  readonly finished: Promise<void>;

  readonly #view: Container;
  readonly #homeY: number;
  readonly #farY: number;
  readonly #farScale: number;

  #passed: number = 0;
  #onArrived: (() => void) | null = null;

  /**
   * @param view Узел машины. Внутри него машина стоит серединой видимой полосы на homeY.
   * @param homeY Где стоит середина видимой полосы, когда машина приехала.
   * @param farY Где она стоит в начале, на экране интро.
   * @param farScale Во сколько раз машина в начале меньше рабочего размера.
   */
  constructor(view: Container, homeY: number, farY: number, farScale: number) {
    this.#view = view;
    this.#homeY = homeY;
    this.#farY = farY;
    this.#farScale = farScale;

    this.finished = new Promise<void>((resolve) => {
      this.#onArrived = resolve;
    });

    this.#place();
  }

  /** Приехала ли машина. */
  get arrived(): boolean {
    return this.#onArrived === null;
  }

  update(seconds: number): void {
    if (this.arrived)
      return;

    this.#passed += seconds;
    this.#place();

    if (this.#passed < ARRIVAL_SECONDS)
      return;

    const arrived: (() => void) | null = this.#onArrived;

    this.#onArrived = null;
    arrived?.();
  }

  /**
   * Ставит машину под пройденную часть пути.
   *
   * Узел растёт от своего начала координат, а машина стоит в нём ниже, поэтому сдвиг считается
   * от её места: иначе она уезжала бы вверх тем сильнее, чем меньше её размер.
   */
  #place(): void {
    const step: number = smoothStep(Math.min(this.#passed / ARRIVAL_SECONDS, 1));
    const scale: number = this.#farScale + (1 - this.#farScale) * step;

    this.#view.scale.set(scale);
    this.#view.y = this.#farY + (this.#homeY - this.#farY) * step - this.#homeY * scale;
  }
}
